
import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import {
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  CountriesService,
  StateSubdivisionOption,
} from "@services/countries.service";
import { GeoAddressParts } from "@services/google-maps.service";
import { SearchPostalCodeService } from "@services/search-postal-code.service";
import { NgxMaskDirective } from "ngx-mask";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";
import { countriesList } from "../../../../../utils/constants/countries.constant";
import { AddressFormGroup } from "./address-form.form";

@Component({
    selector: "app-address-form",
    templateUrl: "./address-form.component.html",
    imports: [
    ReactiveFormsModule,
    NgxMaskDirective,
    MatTooltipModule
]
})
export class AddressFormComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly searchPostalCodeService = inject(SearchPostalCodeService);
  private readonly countriesService = inject(CountriesService);

  @Input() formGroup!: FormGroup<AddressFormGroup>;
  @Input() showAddressName = false;

  isBrazilianCustomer = true;
  isLoadingCep = false;
  countryCode = "BR";

  statesOptions: StateSubdivisionOption[] = [];
  countriesList = countriesList;

  ngOnInit(): void {
    this.subscribeToCountryInput();
    this.subscribeToCepInput();

    const initialCountry = this.formGroup.controls.country?.value || "BR";
    this.applyCountryRules(initialCountry);
    this.loadSubdivisions(initialCountry);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // se você usa pra erro geral
  validate(): ValidationErrors | null {
    return this.formGroup.valid ? null : { addressInvalid: true };
  }

  markAsTouched(): void {
    this.formGroup.markAllAsTouched();
  }

  stateOptionValue(option: StateSubdivisionOption): string {
    return this.countriesService.toFormValue(option, this.countryCode);
  }

  applyGeoResult(parts: GeoAddressParts): void {
    const matched = this.countriesService.matchSubdivision(
      this.countryCode,
      parts.stateLong,
      parts.stateShort,
    );
    const nextState = matched
      ? this.stateOptionValue(matched)
      : this.countryCode === "BR"
        ? parts.stateShort
        : parts.stateLong;

    if (nextState) {
      this.ensureStateOption(nextState, parts.stateLong || nextState);
    }

    const current = this.formGroup.getRawValue();
    this.formGroup.patchValue(
      {
        street: current.street || parts.street || "",
        district: current.district || parts.district || "",
        city_name: current.city_name || parts.city || "",
        state: current.state || nextState || "",
      },
      { emitEvent: false },
    );
    this.formGroup.updateValueAndValidity({ emitEvent: false });
  }

  private subscribeToCountryInput(): void {
    const countryCtrl = this.formGroup.controls.country;

    countryCtrl?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(countryCtrl.value),
        map((country) => (country?.trim() || "BR").toUpperCase()),
        distinctUntilChanged(),
      )
      .subscribe((country) => {
        if (countryCtrl.value !== country) {
          countryCtrl.setValue(country, { emitEvent: false });
        }

        this.applyCountryRules(country);
        this.loadSubdivisions(country);
      });
  }

  private applyCountryRules(country: string): void {
    const stateCtrl = this.formGroup.controls.state;

    this.countryCode = country;
    this.isBrazilianCustomer = country === "BR";

    if (this.isBrazilianCustomer) {
      stateCtrl.setValidators([Validators.required]);
    } else {
      stateCtrl.setValidators([Validators.required, Validators.maxLength(80)]);
    }

    stateCtrl.updateValueAndValidity({ emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false });
  }

  private subscribeToCepInput(): void {
    const postalCtrl = this.formGroup.controls.postal_code;

    postalCtrl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        map((v) => this.sanitizeCep(v)),
        distinctUntilChanged(),
        filter((cep) => this.verifyCep(cep)),
        tap(() => {
          this.isLoadingCep = true;
        }),
        switchMap((cep) =>
          this.searchPostalCodeService.getPostalCode(cep).pipe(
            finalize(() => {
              this.isLoadingCep = false;
            }),
          ),
        ),
      )
      .subscribe((cepResponse) => {
        if (!cepResponse) return;

        this.formGroup.patchValue(
          {
            street: cepResponse.logradouro ?? "",
            district: cepResponse.bairro ?? "",
            city_name: cepResponse.localidade ?? "",
            state: cepResponse.uf ?? "",
            additional_information: cepResponse.complemento ?? "",
          },
          { emitEvent: false },
        );

        this.formGroup.updateValueAndValidity({ emitEvent: false });
      });
  }

  private sanitizeCep(value: unknown): string {
    return String(value ?? "").replace(/\D/g, "");
  }

  private verifyCep(value: unknown): value is string {
    if (!this.isBrazilianCustomer) return false;
    const cep = this.sanitizeCep(value);
    return cep.length === 8 && !/^0+$/.test(cep);
  }

  private loadSubdivisions(countryCode: string): void {
    const stateCtrl = this.formGroup.controls.state;
    this.statesOptions = this.countriesService.getSubdivisions(countryCode);

    const currentState = stateCtrl.value;
    if (!currentState) {
      return;
    }

    const hasCurrentState = this.statesOptions.some(
      (opt) => this.stateOptionValue(opt) === currentState,
    );
    if (!hasCurrentState) {
      stateCtrl.setValue("", { emitEvent: false });
    }
  }

  private ensureStateOption(value: string, label: string): void {
    const exists = this.statesOptions.some(
      (option) => this.stateOptionValue(option) === value || option.name === value,
    );
    if (exists || !value) {
      return;
    }

    this.statesOptions = [
      ...this.statesOptions,
      { code: value, shortCode: value, name: label },
    ].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }
}
